import { Injectable, inject, Injector } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, throwError, of, filter } from 'rxjs';
import { catchError, tap, switchMap, finalize, take } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { ApiService } from '@aeCore/services/api.service';

@Injectable({ providedIn: 'root' })
export class TokenService extends ApiService {
  private router = inject(Router);
  private jwtHelper = inject(JwtHelperService);

  private readonly ACCESS_TOKEN_KEY = 'access_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Expose refresh token observable for components
  public refreshToken$ = this.refreshTokenSubject.asObservable();

  constructor(protected override injector: Injector) {
    super(injector);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  saveTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    this.refreshTokenSubject.next(accessToken);
  }

  removeTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem('user');
    this.refreshTokenSubject.next(null);
  }

  isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    return !token || this.jwtHelper.isTokenExpired(token);
  }

  refreshToken(): Observable<{ accessToken: string; refreshToken: string }> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.router.navigate(['/auth']);
      return throwError(() => new Error('No refresh token available'));
    }

    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter((token) => token !== null),
        take(1),
        switchMap((token) =>
          of({ accessToken: token!, refreshToken: refreshToken }),
        ),
      );
    }

    const headers = new HttpHeaders({
      'refresh-token': 'true',
      Authorization: `Bearer ${refreshToken}`,
    });
    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    return this.post<{
      accessToken: string;
      refreshToken: string;
    }>(['auth', 'refresh'], {}, { headers }).pipe(
      tap((tokens) => {
        this.saveTokens(tokens.accessToken, tokens.refreshToken);
      }),
      catchError((error) => {
        this.removeTokens();
        this.router.navigate(['auth/sign-in']);
        return throwError(() => error);
      }),
      finalize(() => {
        this.isRefreshing = false;
      }),
    );
  }
}
